import React, { useEffect, useState } from 'react';
import {
  ShoppingBag, FileText, Inbox, Truck, Printer, Hammer, Package, Boxes, Users,
  Store, Megaphone, Star, CircleDot, ShoppingCart, TrendingUp, StickyNote, Activity, Trash2,
  Plus, Receipt, Gift, ClipboardCheck, Workflow, RefreshCw, LogOut, PanelLeftClose, PanelLeftOpen, Menu, X, CheckCircle2, Link2,
} from 'lucide-react';
import { cn } from './Table';
import { Badge } from './arc/badge';

// Menú lateral del admin (estilo SaaS) — 10/10/2026.
//
// Escrito acá a propósito: ninguna de las librerías acordadas trae un sidebar libre
// (uiarc no tiene; kobra es paga). Se arma con Badge de uiarc + lucide + Tailwind sobre los
// tokens `.tw`. Reemplaza la cabecera con botones + la barra de pestañas + las sub-barras
// (Productos / Producción / Clientes / Órdenes): cada pantalla es un ítem con su URL.
//
// Desktop: fijo a la izquierda, colapsable a íconos (se recuerda en el navegador).
// Mobile (< lg): barra superior con ☰ que abre el menú como cajón.

export interface NavItem { key: string; label: string; icon: React.ReactNode; badge?: number; hidden?: boolean }
export interface NavGroup { label: string; items: NavItem[] }

const I = (C: any) => <C size={17} strokeWidth={1.8} />;

// Secciones con plata del negocio (costos, márgenes, facturación, gastos): el rol 'staff' no las ve
// (20/10/2026). Grupos enteros Productos (costos/márgenes/receta) y Finanzas.
export const STAFF_HIDDEN_TABS = ['products', 'insumos', 'providers', 'meli', 'wholesale', 'costs', 'consignment'];

export function buildNav(o: { escalated: number; notes: number; trash: number; meliConnected: boolean; staff?: boolean }): NavGroup[] {
  const groups: NavGroup[] = [
    { label: 'Ventas', items: [
      { key: 'orders', label: 'Órdenes', icon: I(ShoppingBag) },
      { key: 'budgets', label: 'Presupuestos', icon: I(FileText) },
      { key: 'transfers', label: 'Atención', icon: I(Inbox), badge: o.escalated },
      { key: 'cabify', label: 'Envíos Cabify', icon: I(Truck) },
      { key: 'despacho', label: 'Control de despacho', icon: I(ClipboardCheck) },
      { key: 'print', label: 'Etiquetas', icon: I(Printer) },
    ] },
    { label: 'Producción', items: [
      { key: 'taller', label: 'Taller', icon: I(Hammer) },
    ] },
    { label: 'Productos', items: [
      { key: 'products', label: 'Catálogo', icon: I(Package) },
      { key: 'insumos', label: 'Insumos', icon: I(Boxes) },
      { key: 'providers', label: 'Proveedores', icon: I(Users) },
      { key: 'meli', label: 'Publicaciones ML', icon: I(Megaphone), hidden: !o.meliConnected },
      { key: 'wholesale', label: 'Mayorista', icon: I(Store) },
    ] },
    { label: 'Clientes', items: [
      { key: 'reviews', label: 'Reviews', icon: I(Star) },
      { key: 'circulo_rojo', label: 'Círculo Rojo', icon: I(CircleDot) },
      { key: 'carritos', label: 'Carritos abandonados', icon: I(ShoppingCart) },
    ] },
    { label: 'Finanzas', items: [
      { key: 'costs', label: 'Costos & BI', icon: I(TrendingUp) },
      { key: 'consignment', label: 'La Suite', icon: I(Store) },
    ] },
    { label: 'Herramientas', items: [
      { key: 'notes', label: 'Notas', icon: I(StickyNote), badge: o.notes },
      { key: 'activity', label: 'Actividad', icon: I(Activity) },
      { key: 'mapa', label: 'Mapa de mensajes', icon: I(Workflow) },
      { key: 'trash', label: 'Papelera', icon: I(Trash2), badge: o.trash },
    ] },
  ];
  if (!o.staff) return groups;
  return groups
    .map(g => ({ ...g, items: g.items.filter(it => !STAFF_HIDDEN_TABS.includes(it.key)) }))
    .filter(g => g.items.length > 0);
}

const COLLAPSE_KEY = 'krilic.sidebar.collapsed';

export function AppSidebar({
  groups, active, onNavigate, onNewSale, onNewExpense, onNewCanje, onSync, syncing,
  tnConnected, meliConnected, meliNickname, onConnectTn, onConnectMeli, connectionsKnown = true,
  bell, email, onLogout, children,
}: {
  groups: NavGroup[];
  active: string;
  onNavigate: (key: string) => void;
  onNewSale: () => void;
  /** Sin esto no se muestra "Nuevo gasto" (rol staff). */
  onNewExpense?: () => void;
  /** "Nuevo canje" (rol staff). */
  onNewCanje?: () => void;
  onSync: () => void;
  syncing?: boolean;
  tnConnected: boolean;
  meliConnected: boolean;
  /** false mientras se consulta: no se muestra "Conectar…" (evita el parpadeo en rojo). */
  connectionsKnown?: boolean;
  meliNickname?: string | null;
  onConnectTn: () => void;
  onConnectMeli: () => void;
  bell?: React.ReactNode;
  email?: string | null;
  onLogout: () => void;
  children: React.ReactNode;
}) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => { try { localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0'); } catch { /* noop */ } }, [collapsed]);
  // al navegar en mobile se cierra el cajón
  const go = (k: string) => { onNavigate(k); setMobileOpen(false); };
  const activeLabel = groups.flatMap(g => g.items).find(i => i.key === active)?.label || '';

  const panel = (isDrawer: boolean) => {
    const mini = collapsed && !isDrawer;
    return (
      <div className="flex h-full flex-col">
        {/* Marca + colapsar */}
        <div className={cn('flex h-14 shrink-0 items-center border-b border-line px-3', mini ? 'justify-center' : 'justify-between')}>
          {!mini && <img src="/logo.png" alt="Krilic" className="h-6 w-auto object-contain" />}
          {isDrawer
            ? <button type="button" aria-label="Cerrar menú" onClick={() => setMobileOpen(false)} className="grid h-8 w-8 place-items-center rounded-[var(--radius-control)] text-faint hover:bg-muted hover:text-ink"><X size={18} /></button>
            : <button type="button" aria-label={mini ? 'Expandir menú' : 'Colapsar menú'} title={mini ? 'Expandir menú' : 'Colapsar menú'}
                onClick={() => setCollapsed(c => !c)}
                className="grid h-8 w-8 place-items-center rounded-[var(--radius-control)] text-faint hover:bg-muted hover:text-ink">
                {mini ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
              </button>}
        </div>

        {/* Acciones principales */}
        <div className={cn('grid shrink-0 gap-1.5 p-3', mini && 'px-2')}>
          <button type="button" onClick={() => { onNewSale(); setMobileOpen(false); }} title="Nueva venta"
            className={cn('flex h-9 items-center gap-2 rounded-[var(--radius-control)] bg-[var(--foreground)] text-[length:var(--text-sm)] font-medium text-[var(--card)] transition-opacity hover:opacity-90',
              mini ? 'justify-center px-0' : 'px-3')}>
            <Plus size={16} />{!mini && 'Nueva venta'}
          </button>
          {onNewExpense && (
          <button type="button" onClick={() => { onNewExpense(); setMobileOpen(false); }} title="Nuevo gasto"
            className={cn('flex h-9 items-center gap-2 rounded-[var(--radius-control)] border border-line text-[length:var(--text-sm)] font-medium text-ink transition-colors hover:bg-muted',
              mini ? 'justify-center px-0' : 'px-3')}>
            <Receipt size={16} />{!mini && 'Nuevo gasto'}
          </button>
          )}
          {onNewCanje && (
          <button type="button" onClick={() => { onNewCanje(); setMobileOpen(false); }} title="Nuevo canje"
            className={cn('flex h-9 items-center gap-2 rounded-[var(--radius-control)] border border-line text-[length:var(--text-sm)] font-medium text-ink transition-colors hover:bg-muted',
              mini ? 'justify-center px-0' : 'px-3')}>
            <Gift size={16} />{!mini && 'Nuevo canje'}
          </button>
          )}
        </div>

        {/* Navegación */}
        <nav className={cn('min-h-0 flex-1 overflow-y-auto pb-3', mini ? 'px-2' : 'px-3')} aria-label="Secciones">
          {groups.map(g => {
            const items = g.items.filter(i => !i.hidden);
            if (!items.length) return null;
            return (
              <div key={g.label} className="mt-3 first:mt-0">
                {!mini
                  ? <div className="px-2 pb-1 text-[length:var(--text-xs)] font-medium uppercase tracking-wide text-faint">{g.label}</div>
                  : <div className="mx-2 mb-1 border-t border-line" />}
                <ul className="m-0 grid list-none gap-0.5 p-0">
                  {items.map(it => {
                    const on = it.key === active;
                    return (
                      <li key={it.key}>
                        <button type="button" onClick={() => go(it.key)} title={mini ? it.label : undefined}
                          aria-current={on ? 'page' : undefined}
                          className={cn('relative flex h-9 w-full items-center gap-2.5 rounded-[var(--radius-control)] text-left text-[length:var(--text-sm)] transition-colors',
                            mini ? 'justify-center px-0' : 'px-2',
                            on ? 'bg-muted font-medium text-ink' : 'text-soft hover:bg-muted hover:text-ink')}>
                          {on && <span className="absolute left-0 top-1.5 h-6 w-[3px] rounded-full bg-[var(--brand)]" aria-hidden="true" />}
                          <span className={cn('shrink-0', on ? 'text-ink' : 'text-faint')}>{it.icon}</span>
                          {!mini && <span className="min-w-0 flex-1 truncate">{it.label}</span>}
                          {!!it.badge && it.badge > 0 && (mini
                            ? <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-[var(--danger)]" aria-label={`${it.badge} pendientes`} />
                            : <Badge size="sm" tone={it.key === 'trash' ? 'neutral' : 'danger'}>{it.badge}</Badge>)}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </nav>

        {/* Pie: conexiones, sincronizar, notificaciones, usuario */}
        <div className={cn('shrink-0 border-t border-line p-3', mini && 'px-2')}>
          {!mini && connectionsKnown && (
            <div className="mb-2 grid gap-1 text-[length:var(--text-xs)]">
              {tnConnected
                ? <span className="flex items-center gap-1.5 text-soft"><CheckCircle2 size={13} className="text-[var(--success)]" /> Tiendanube conectada</span>
                : <button type="button" onClick={onConnectTn} className="flex items-center gap-1.5 font-medium text-[var(--danger)] hover:underline"><Link2 size={13} /> Conectar Tiendanube</button>}
              {meliConnected
                ? <span className="flex items-center gap-1.5 truncate text-soft"><CheckCircle2 size={13} className="text-[var(--success)]" /> ML · {meliNickname || 'conectada'}</span>
                : <button type="button" onClick={onConnectMeli} className="flex items-center gap-1.5 font-medium text-[var(--danger)] hover:underline"><Link2 size={13} /> Conectar MercadoLibre</button>}
            </div>
          )}
          <div className={cn('flex items-center gap-1', mini ? 'flex-col' : '')}>
            <button type="button" onClick={onSync} disabled={syncing} title="Sincronizar órdenes (Tiendanube + MercadoLibre)"
              className={cn('flex h-8 items-center gap-1.5 rounded-[var(--radius-control)] border border-line text-[length:var(--text-xs)] font-medium text-ink transition-colors hover:bg-muted disabled:opacity-60',
                mini ? 'w-8 justify-center' : 'flex-1 justify-center px-2')}>
              <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />{!mini && (syncing ? 'Sincronizando…' : 'Sincronizar')}
            </button>
            {bell}
          </div>
          <div className={cn('mt-2 flex items-center gap-2', mini && 'justify-center')}>
            {!mini && <span className="min-w-0 flex-1 truncate text-[length:var(--text-xs)] text-faint" title={email || ''}>{email}</span>}
            <button type="button" onClick={onLogout} aria-label="Cerrar sesión" title="Cerrar sesión"
              className="grid h-8 w-8 shrink-0 place-items-center rounded-[var(--radius-control)] text-faint hover:bg-muted hover:text-ink">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    // OJO: `tw` (tokens + reset acotado) va SOLO en el menú y la barra móvil, no en el
    // contenedor: envolver todo el admin le sacaría el estilo a los controles legacy.
    <div className="flex min-h-screen">
      {/* Desktop */}
      <aside className={cn('tw sticky top-0 z-40 hidden h-screen shrink-0 border-r border-line bg-card transition-[width] duration-200 lg:block',
        collapsed ? 'w-16' : 'w-60')}>
        {panel(false)}
      </aside>

      {/* Mobile: barra superior + cajón */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[1000] lg:hidden" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-[rgba(0,0,0,0.5)]" onClick={() => setMobileOpen(false)} />
          <aside className="tw absolute inset-y-0 left-0 w-72 max-w-[85vw] border-r border-line bg-card shadow-[var(--shadow-floating)] animate-in slide-in-from-left">
            {panel(true)}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="tw sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-line bg-card px-4 lg:hidden">
          <button type="button" aria-label="Abrir menú" onClick={() => setMobileOpen(true)}
            className="grid h-9 w-9 place-items-center rounded-[var(--radius-control)] text-ink hover:bg-muted"><Menu size={20} /></button>
          <img src="/logo.png" alt="Krilic" className="h-5 w-auto object-contain" />
          <span className="truncate text-[length:var(--text-sm)] font-medium text-soft">{activeLabel}</span>
          <button type="button" onClick={onNewSale} aria-label="Nueva venta"
            className="ml-auto grid h-9 w-9 place-items-center rounded-[var(--radius-control)] bg-[var(--foreground)] text-[var(--card)]"><Plus size={18} /></button>
        </header>
        {children}
      </div>
    </div>
  );
}
